import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, LogOut, LayoutDashboard, Library, Settings2, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { BrandLogo } from './BrandLogo';

const NavItems = ({ onClick, demo = false }) => (
  <>
    {!demo && <NavLink to="/concorsi" onClick={onClick} data-testid="nav-catalogo"
      className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? 'text-brand' : 'text-slate-600 hover:text-brand'}`}>
      Catalogo concorsi
    </NavLink>}
  </>
);

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/70">
      {user && user.email_verified !== true && <div className="bg-amber-50 px-4 py-2 text-sm text-center"><Link to="/verifica-email" className="underline">Verifica la tua email per effettuare nuovi acquisti</Link></div>}
      <div className="page-shell h-[4.5rem] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <BrandLogo className="text-[1.05rem] sm:text-lg" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavItems demo={user?.role === 'PARTNER_DEMO'} />
          {user && (
            <NavLink to="/area-personale" data-testid="nav-libreria"
              className={({ isActive }) => `text-sm font-medium transition-colors ${isActive ? 'text-brand' : 'text-slate-600 hover:text-brand'}`}>
              La mia libreria
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user?.role !== 'PARTNER_DEMO' && <Button variant="ghost" onClick={() => navigate('/concorsi')} aria-label="Cerca un concorso" className="h-10 w-10 rounded-full p-0 text-slate-500">
            <Search size={18} />
          </Button>}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1 pr-3 py-1 hover:border-brand transition-colors">
                  <span className="w-7 h-7 rounded-full bg-brand-light text-brand flex items-center justify-center text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </span>
                  <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/area-personale')} data-testid="menu-libreria">
                  <Library className="w-4 h-4 mr-2" strokeWidth={1.5} /> La mia libreria
                </DropdownMenuItem>
                {user.role !== 'PARTNER_DEMO' && <DropdownMenuItem onClick={() => navigate('/account')} data-testid="menu-account">
                  <Settings2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Il mio account
                </DropdownMenuItem>}
                {user.role === 'ADMIN' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} /> Pannello admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate('/accedi')} data-testid="nav-login" className="text-sm">Accedi</Button>
              <Button onClick={() => navigate('/accedi?mode=register')} data-testid="nav-register"
                className="rounded-full bg-brand hover:bg-brand-hover text-white text-sm px-5">
                Registrati
              </Button>
            </>
          )}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="md:hidden min-h-11 min-w-11 p-2" data-testid="nav-mobile-menu" aria-label="Apri menu">
              <Menu className="w-6 h-6 text-slate-800" strokeWidth={1.5} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72">
            <div className="flex flex-col gap-6 mt-8">
              <NavItems demo={user?.role === 'PARTNER_DEMO'} onClick={() => setOpen(false)} />
              {user && (
                <NavLink to="/area-personale" onClick={() => setOpen(false)} data-testid="nav-libreria-mobile" className="text-sm font-medium text-slate-600">
                  La mia libreria
                </NavLink>
              )}
              {user?.role === 'ADMIN' && (
                <NavLink to="/admin" onClick={() => setOpen(false)} data-testid="nav-admin-mobile" className="text-sm font-medium text-slate-600">
                  Pannello admin
                </NavLink>
              )}
              <div className="border-t border-slate-200 pt-6 flex flex-col gap-3">
                {user ? (
                  <Button variant="outline" onClick={handleLogout} data-testid="nav-logout-mobile" className="rounded-full">Esci</Button>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => { setOpen(false); navigate('/accedi'); }} data-testid="nav-login-mobile" className="rounded-full">Accedi</Button>
                    <Button onClick={() => { setOpen(false); navigate('/accedi?mode=register'); }} data-testid="nav-register-mobile" className="rounded-full bg-brand hover:bg-brand-hover text-white">Registrati</Button>
                  </>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
